import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { List as ImmutableList } from 'immutable';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import compose from 'recompose/compose';
import { connect } from 'react-redux';

import { getFeatureFlags } from 'utils/featureFlags/selectors';
import { createTag } from 'data/tags/actions';
import { getTags } from 'data/tags/selectors';
import { mkTag } from 'data/tags/types';

import { InputAdornment } from '@material-ui/core';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@material-ui/icons/ExpandLessRounded';
import QIconButton from 'components/QIconButton';
import NestedQMenu from 'components/NestedQMenu';
import QTypography from 'components/MUIWrappers/QTypography';
import ChippedInput from './ChippedInput';
import InputChip from './InputChip'

export function findTagName(tags, id) {
  const tag = tags.find((x) => String(x.id) === String(id));
  return tag ? tag.name : '';
}

export function findTagId(tags, name) {
  const tag = tags.find((x) => x.name.toLowerCase() === name.toLowerCase());
  return tag ? tag.id : '0';
}

export function makeTagNameArray(tags, txnTags) {
  let txString = '';
  if (txnTags) {
    txString = txnTags.reduce((res, item) => {
      //
      // if there is no name match yet (no id from QCS yet on create), then
      // preserve the name that was given
      //
      const name = findTagName(tags, item.id);
      return name.length > 0 ? `${res}${name},` : `${res}${item.name},`;
    }, '');
  }

  return txString;
}

const styles = (_theme) => ({
  root: {
    flexGrow: 1,
  },
});

class TagsField extends React.PureComponent {

  constructor(props) {
    super(props);
    this.state = {
      showMenu: false,
      query: null,
    };
    this.currentHover = null;
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.allTags !== this.props.allTags && nextProps.value) {
      const { allTags, value } = nextProps;
      const updatedTagArray = [];

      let newTagFound = false;
      value.forEach((tag) => {
        if (tag.id === null && tag.name && tag.name !== 'unknown') { // need to replace with real tag
          updatedTagArray.push(mkTag({
            ...tag,
            id: findTagId(allTags, tag.name),
            name: tag.name,
          }));
          newTagFound = true;
        } else {
          updatedTagArray.push(tag);
        }
      });

      if (newTagFound) this.props.onChange(new ImmutableList(updatedTagArray));
    }
  }

  createNewTag = (tagName, clientId) => {
    const name = tagName?.trim();
    if (assert(name, `valid tag name required "${tagName}" -> "${name}"`)) {
      const tagToCreate = mkTag({
        clientId,
        name,
      });
      this.props.createTag(tagToCreate);
    }
  };

  optionSort = ({ label: firstName }, { label: secondName }) => {
    if (firstName.toLowerCase() < secondName.toLowerCase()) return -1;
    if (firstName.toLowerCase() > secondName.toLowerCase()) return 1;
    return 0;
  };

  createMenuOptions = (allTags, valueTags) => {
    const menuOptions = [];

    allTags.keySeq().forEach((tagId) => {
      let hasTag = false;
      if (valueTags) {
        valueTags.forEach((tag) => {
          if (tag.id === tagId) {
            // if the ids match
            hasTag = true;
          } else if (tag.name && tag.name !== 'unknown' && findTagName(allTags, tagId) === tag.name) {
            // if the tag has a name and it matches in the list
            hasTag = true;
          }
        });
      }
      if (!hasTag) {
        const tagName = findTagName(allTags, tagId);
        menuOptions.push({
          label: tagName,
          value: { tagInfo: { id: tagId, name: tagName }, name: tagName },
        })
      }
    });

    if (this.state.query) {
      const filteredMenuOptions = menuOptions.map((option) => {
        const matchIndex = option.label.toLowerCase().indexOf(this.state.query.toLowerCase());
        if (matchIndex === -1) return null;
        return {
          label: option.label,
          value: option.value,
          customRender: this.buildMatchedLabel(option.label, matchIndex),
        };
      }).filter((op) => op !== null).sort(this.optionSort);

      if (!filteredMenuOptions.find((option) => option.label.toLowerCase() === this.state.query.toLowerCase())
        && findTagId(allTags, this.state.query.toLowerCase()) === '0') {
        const { query } = this.state;
        if (query?.trim?.()) {
          filteredMenuOptions.push({
            label: query,
            value: {tagInfo: {id: null, name: query}, name: query, clientId: uuidv4()},
            customRender: this.buildMatchedLabel(`Create "${query}"`, 8),
          })
        }
      }

      // sorted before adding the create option
      return filteredMenuOptions;
    }
    return menuOptions.sort(this.optionSort);
  };

  createTagChips = () => {
    const { allTags, value } = this.props;
    return value ? value.map((tag, index) => (
      <InputChip
        key={`tag-field-chip-${tag.id}`}
        label={tag.name && tag.name !== 'unknown' ? tag.name : findTagName(allTags, tag.id)}
        onDelete={this.handleTagRemove}
        hovered={index === value.size-1 && this.state.hoverLastChip}
      />
    )) : [];
  };

  buildMatchedLabel = (label, indexOfMatch) => (
    <QTypography>
      {label.slice(0,indexOfMatch)}
      <span style={{ fontWeight: this.props.theme.typography.fontWeightMedium }} >{label.slice(indexOfMatch, indexOfMatch+this.state.query.length)}</span>
      {label.slice(indexOfMatch+this.state.query.length)}
    </QTypography>
  );

  handleChange = (value) => {
    if (!this.state.hoverLastChip)
      this.setState({ query: value !== '' || value !== ' ' ? value : null });
  };

  handleKeyDelete = (isHover) => (val) => {
    if (isHover && val) { // onHoverDelete and is hovering
      this.setState({ hoverLastChip: true });
    } else if (isHover && !val) { // onHoverDelete and not hovering
      this.setState({ hoverLastChip: false });
    } else if (!isHover && this.props.value && this.props.value.size !== 0) {  // delete last chip
      this.handleTagRemove(findTagName(this.props.allTags, this.props.value.get(this.props.value.size-1).id),
        { stopPropagation: () => 0 });
    }
  };

  handleKeyDown = (event) => {
    if (this.props.tagsCharFilter && this.props.tagsCharFilter.includes(event.key)) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (event.key === 'Enter' && this.currentHover) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    if (event.key === 'Tab' && this.currentHover) {
      this.menuOnChange(this.currentHover.value);
    } else if (event.key === 'ArrowDown' && !this.state.showMenu) {
      this.setState({ showMenu: true })
    }
    if (this.props.onKeyDown) {
      this.props.onKeyDown(event);
    }
  };

  handleCurrentHover = (current) => {
    if (this.currentHover !== current)
      this.currentHover = current;
  };

  handleTagRemove = (label, event) => {
    event.stopPropagation();

    const { allTags, value } = this.props;

    const tagId = findTagId(allTags, label);
    const updatedTagArray = [];

    value.forEach((tag) => {
      if (tagId && tag.id !== tagId) {
        updatedTagArray.push(mkTag({
          ...tag,
          id: tag.id,
          name: tag.name && tag.name !== 'unknown' ? tag.name : findTagName(allTags, tag.id),
        }));
      }
    });

    this.props.onChange(new ImmutableList(updatedTagArray));
  };

  dropDownClick = () => this.setState({ showMenu: !this.state.showMenu, query: null });

  menuOnChange = (tag) => {
    const { allTags, value } = this.props;
    const updatedTagArray = [];

    if (value) {
      value.forEach((tagValue) => {
        updatedTagArray.push(mkTag({
          ...tagValue,
          id: tagValue.id,
          name: tagValue.name && tagValue.name !== 'unknown' ? tagValue.name : findTagName(allTags, tagValue.id),
        }));
      })
    }

    if (tag.tagInfo.id === null) {
      this.createNewTag(tag.name, tag.clientId);
    }

    updatedTagArray.push(mkTag({
      id: (tag.tagInfo && tag.tagInfo.id),
      name: tag.name,
      clientId: tag.clientId || null,
    }));

    this.props.onChange(new ImmutableList(updatedTagArray));

    this.setState({ showMenu: false, query: null });
  };

  render() {

    const { classes, autoFocus, name, margin, label, InputProps, fullWidth,
      placeholder, fieldId, id, value, allTags, bulkEdit, theme } = this.props;
    const { query, showMenu } = this.state;

    const menuOpen = showMenu || query !== null;

    const menuIcon = <QIconButton
      style={{ color: theme.palette.grey.level6, cursor: 'pointer', fontSize: '23px' }}
      aria-label="Open Tag List"
      focusRipple
      IconComponent={this.state.showMenu || this.state.query !== null ? ExpandLessRoundedIcon : ExpandMoreRoundedIcon}
      onClick={this.dropDownClick}
      size="small-no-padding"
      TooltipProps={{ enterDelay: 10 }}
      tabIndex={-1}
    />;

    return (
      <div
        className={classes.root}
        id={id}
        name={name}
        ref={(x) => { this.fieldRef = x; }}
      >
        <TextField
          name="bogus-name"
          id={fieldId}
          label={label}
          margin={margin}
          className={classes.textField}
          fullWidth={fullWidth}
          value={value || query ? ' ' : ''}
          onKeyDown={this.handleKeyDown}
          autoFocus={autoFocus}
          onClick={this.dropDownClick}
          style={{
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            whiteSpace: 'noWrap',
          }}
          autoComplete="off"
          InputProps={{
            ...InputProps,
            inputComponent: ChippedInput,
            disableUnderline: this.props.disableUnderline,
            name: 'testing',
            // these are props being passed down to ChippedInput
            inputProps: {
              placeholder,
              bulkEdit,
              id: 'tag-input',
              maxLength: 32,
              chips: this.createTagChips(),
              onChange: this.handleChange,
              keyDelete: true,
              onHoverDelete: this.handleKeyDelete(true),
              onDelete: this.handleKeyDelete(),
              getInputProps: ({cleanValue}) => { this.textFieldCleanAction = cleanValue; },
              inputRef: (x) => {
                if (this.props.inputRef) {
                  this.props.inputRef(x);
                }
                this.inputRef = x;
              },
            },
            endAdornment: (
              <InputAdornment position="end">
                {menuIcon}
              </InputAdornment>
            ),
          }}
        />
        {menuOpen &&
          <NestedQMenu
            anchorEl={this.fieldRef}
            name="tag-menu"
            options={this.createMenuOptions(allTags, value)}
            onChange={this.menuOnChange}
            open={menuOpen}
            onClose={() => {
              this.textFieldCleanAction && this.textFieldCleanAction(); // Cleans the text field
              this.setState({ showMenu: false, query: null });
            }}
            currentHover={this.handleCurrentHover}
          />
        }
      </div>
    );
  }
}

TagsField.propTypes = {
  classes: PropTypes.object.isRequired,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  inputRef: PropTypes.func,
  autoFocus: PropTypes.bool,
  disableUnderline: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  fieldId: PropTypes.string,
  allTags: PropTypes.object,
  createTag: PropTypes.func,
  label: PropTypes.string,
  margin: PropTypes.string,
  fullWidth: PropTypes.bool,
  placeholder: PropTypes.string,
  InputProps: PropTypes.object,
  bulkEdit: PropTypes.bool,
  theme: PropTypes.object,
  tagsCharFilter: PropTypes.object,
};

TagsField.defaultProps = {
  fullWidth: true,
  placeholder: 'Tags',
  bulkEdit: false,
};

function mapStateToProps(state) {
  return {
    allTags: getTags(state),
    tagsCharFilter: getFeatureFlags(state).get('tagsCharFilter'),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    createTag: (data) => dispatch(createTag(data)),
  };
}

// These are prop injectors
export default compose(
  withStyles(styles, { withTheme: true }),  // creates this.props.classes
)(connect(mapStateToProps, mapDispatchToProps)(TagsField));

